import * as React from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { ListCount } from '@/components/ui/workspace';
import { reviewCount, type KbReview } from '@/lib/api/vendor-knowledge';
import { Section, SectionHead } from '../kb-bits';
import { plural, skipReasonText } from '../kb-logic';
import { ProposedGroups } from './proposals';
import { HeldGroups } from './held';
import { FlagGroups } from './flags';
import { DueGroup } from './due';
import type { ReviewResult } from './review-bits';

/**
 * To review, for one vendor (prototype `vReview()`; E13-F06, E13-F07): what
 * AI proposed from new captures, proposals held for a look, flagged items
 * and items due for review, each in its own group with its reason. The
 * counts are the server's (`GET /review?vendor_id`). Nothing here is used
 * until a curator decides.
 */
export function ReviewSection({
  vendorId,
  review,
  extraGroups,
  extraCount = 0,
}: {
  vendorId: string;
  review: KbReview | undefined;
  /** Groups added by the sources screens (received files, readings, problems). */
  extraGroups?: React.ReactNode;
  extraCount?: number;
}) {
  const [result, setResult] = React.useState<ReviewResult | null>(null);
  if (!review) return null;
  const proposed = reviewCount(review, 'proposed');
  const held = reviewCount(review, 'held');
  const flagged = reviewCount(review, 'flagged');
  const due = reviewCount(review, 'due_for_review');
  // The decisions on knowledge, as the left list and the summary line count them.
  const toDecide = review.by_vendor.find((v) => v.vendor_id === vendorId)?.count ?? 0;
  const total = proposed + held + flagged + due + extraCount;
  if (total === 0 && !result) return null;

  return (
    <Section id="kb-to-review" label="To review">
      <SectionHead
        title="To review"
        badge={toDecide > 0 ? <ListCount>{toDecide}</ListCount> : undefined}
        hint="Nothing here is used until a curator decides."
      />
      {result ? <ResultNote result={result} onClose={() => setResult(null)} /> : null}
      {proposed > 0 ? (
        <ProposedGroups vendorId={vendorId} count={proposed} onResult={setResult} />
      ) : null}
      {held > 0 ? <HeldGroups vendorId={vendorId} /> : null}
      {flagged > 0 ? <FlagGroups vendorId={vendorId} /> : null}
      {extraGroups}
      {due > 0 ? <DueGroup vendorId={vendorId} count={due} /> : null}
    </Section>
  );
}

/** What one confirm did: numbers given, passages that became citations, and what was skipped and why. */
function ResultNote({ result, onClose }: { result: ReviewResult; onClose: () => void }) {
  const lines: string[] = [];
  if (result.confirmed.length > 0) {
    lines.push(
      `Confirmed ${plural(result.confirmed.length, 'item')}: ${result.confirmed.map((c) => c.number).join(', ')}.`,
    );
  }
  for (const b of result.becameCitation) {
    lines.push(
      `One means the same as ${b.itemNumber ?? 'an item'} confirmed meanwhile, so it was offered as a further citation of it.`,
    );
  }
  if (result.accepted.length > 0) {
    lines.push(
      `Added ${plural(result.accepted.length, 'further citation')} to ${result.accepted
        .map((a) => a.itemNumber ?? 'an item')
        .join(', ')}.`,
    );
  }
  for (const s of result.skipped) {
    lines.push(`Skipped${s.label ? ` “${s.label}”` : ''}: ${skipReasonText(s.reason)}.`);
  }
  for (const f of result.failed) lines.push(f);
  if (lines.length === 0) lines.push('Nothing was confirmed.');
  return (
    <div
      role="status"
      className="mb-3 flex items-start gap-2.5 rounded-card border border-ink-100 border-l-4 border-l-posture-healthy bg-paper-0 px-4 py-3 text-body-sm text-ink-900"
    >
      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-posture-healthy" aria-hidden="true" />
      <ul className="m-0 min-w-0 flex-1 list-none space-y-0.5 p-0">
        {lines.map((l, k) => (
          <li key={k}>{l}</li>
        ))}
      </ul>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
